/**
 * Sélection d'une trace ou d'une route et construction du parcours.
 * EF-GPX-001, EF-GPX-002, EF-RBK-014.
 */

import { DomainError } from '../core/errors';
import { distance, type Coordinate } from '../geo/geodesy';
import { buildComputedTrack, SEGMENT_GAP_THRESHOLD_METERS, type ComputedTrack, type RawTrack } from '../geo/track';
import { MIN_ROUTE_LENGTH_METERS, type GpxDocument, type GpxElement } from './parser';

/** Description d'un élément proposé à la sélection (EF-GPX-002). */
export interface SelectableElement {
  readonly type: 'trk' | 'rte';
  readonly index: number;
  /** Nom de l'élément ou, à défaut, son type et son numéro d'ordre. */
  readonly label: string;
  readonly name: string | null;
  readonly pointCount: number;
}

export function describeElements(document: GpxDocument): readonly SelectableElement[] {
  return document.elements.map((element) => ({
    type: element.type,
    index: element.index,
    name: element.name,
    label: element.name ?? `${element.type === 'trk' ? 'Trace' : 'Route'} ${element.index + 1}`,
    pointCount: element.count,
  }));
}

export interface ImportWarnings {
  /** Routes entièrement ignorées parce que le fichier contient des traces. */
  readonly ignoredRouteCount: number;
  /** Écarts entre segments supérieurs à 1 km, exigeant une confirmation. */
  readonly segmentGapCount: number;
  readonly largestSegmentGapMeters: number;
  /** Écarts supérieurs à 10 km entre deux points consécutifs d'un segment. */
  readonly pointAnomalyCount: number;
  readonly largestPointAnomalyMeters: number;
  readonly largestPointAnomalyProgressionMeters: number;
  readonly elevationUnavailable: boolean;
}

export interface RouteImport {
  readonly track: ComputedTrack;
  readonly selected: SelectableElement;
  readonly originalPointCount: number;
  readonly warnings: ImportWarnings;
  /** Titre initial du roadbook selon EF-RBK-014. */
  readonly title: string;
}

/** Convertit un élément GPX en points bruts, sans aucun filtrage. */
export function toRawTrack(element: GpxElement): RawTrack {
  return {
    lat: element.lat,
    lon: element.lon,
    ele: element.ele,
    segmentStarts: element.segmentStarts,
    count: element.count,
  };
}

/**
 * Construit le parcours à partir de l'élément choisi et applique les contrôles
 * qui n'ont de sens qu'après la sélection.
 */
export function buildRoute(
  document: GpxDocument,
  elementIndex: number,
  sourceFileName: string | null,
): RouteImport {
  const element = document.elements[elementIndex];
  if (element === undefined) {
    throw new DomainError('gpx/no-usable-track', "L'élément choisi n'existe pas dans le fichier.");
  }

  if (countDistinctPoints(element) < 2) {
    throw new DomainError(
      'gpx/no-usable-track',
      'Aucun parcours exploitable : une trace ou une route contenant au moins deux points différents est requise.',
    );
  }

  const raw = toRawTrack(element);
  const length = rawLength(raw);
  if (length < MIN_ROUTE_LENGTH_METERS) {
    throw new DomainError(
      'gpx/too-short',
      `Le parcours mesure ${formatKilometers(length)}, en deçà de la longueur minimale de 1,0 km.`,
      String(length),
    );
  }

  const track = buildComputedTrack(raw);
  const selected = describeElements(document)[elementIndex];
  if (selected === undefined) {
    throw new DomainError('gpx/no-usable-track', "L'élément choisi n'existe pas dans le fichier.");
  }

  return {
    track,
    selected,
    originalPointCount: element.count,
    warnings: summarizeWarnings(document, track),
    title: initialTitle(element, sourceFileName),
  };
}

function summarizeWarnings(document: GpxDocument, track: ComputedTrack): ImportWarnings {
  let largestGap = 0;
  for (const gap of track.segmentGaps) largestGap = Math.max(largestGap, gap.distanceMeters);

  let largestAnomaly = 0;
  let largestAnomalyProgression = 0;
  for (const anomaly of track.pointAnomalies) {
    if (anomaly.distanceMeters > largestAnomaly) {
      largestAnomaly = anomaly.distanceMeters;
      largestAnomalyProgression = anomaly.afterProgressionMeters;
    }
  }

  return {
    ignoredRouteCount: document.ignoredRouteCount,
    segmentGapCount: track.segmentGaps.length,
    largestSegmentGapMeters: largestGap,
    pointAnomalyCount: track.pointAnomalies.length,
    largestPointAnomalyMeters: largestAnomaly,
    largestPointAnomalyProgressionMeters: largestAnomalyProgression,
    elevationUnavailable: !track.elevation.available,
  };
}

/**
 * Titre initial : nom de la trace ou de la route, à défaut nom du fichier privé
 * de son extension, à défaut « Roadbook » (EF-RBK-014).
 */
export function initialTitle(element: GpxElement, sourceFileName: string | null): string {
  if (element.name !== null && element.name.trim().length > 0) return element.name.trim();
  if (sourceFileName !== null) {
    const withoutExtension = sourceFileName.replace(/\.gpx$/i, '').trim();
    if (withoutExtension.length > 0) return withoutExtension;
  }
  return 'Roadbook';
}

function countDistinctPoints(element: GpxElement): number {
  const seen = new Set<string>();
  for (let i = 0; i < element.count; i += 1) {
    const lat = element.lat[i];
    const lon = element.lon[i];
    if (lat === undefined || lon === undefined) continue;
    seen.add(`${lat},${lon}`);
    if (seen.size >= 2) return 2;
  }
  return seen.size;
}

function rawLength(raw: RawTrack): number {
  let total = 0;
  for (let i = 1; i < raw.count; i += 1) {
    const previous = pointAt(raw, i - 1);
    const current = pointAt(raw, i);
    total += distance(previous, current);
  }
  return total;
}

function pointAt(raw: RawTrack, index: number): Coordinate {
  const lat = raw.lat[index];
  const lon = raw.lon[index];
  if (lat === undefined || lon === undefined) throw new Error(`Indice hors trace : ${index}`);
  return { lat, lon };
}

function formatKilometers(meters: number): string {
  return `${(meters / 1000).toFixed(1).replace('.', ',')} km`;
}

/** Seuil d'écart entre segments exigeant une confirmation avant importation. */
export const SEGMENT_GAP_CONFIRMATION_METERS = SEGMENT_GAP_THRESHOLD_METERS;
